'use client';

import { ReactNode, useState, createContext, useContext } from 'react';
import { Icon } from './icon';
import { twMerge } from 'tailwind-merge';

type AccordionContextType = {
  activeIndex: number | null;
  setActiveIndex: (index: number | null) => void;
};

const AccordionContext = createContext<AccordionContextType | undefined>(
  undefined
);

type AccordionWrapperProps = {
  children: ReactNode;
  title?: string;
  className?: string;
  titleClassName?: string;
};

export const AccordionWrapper = ({
  children,
  title,
  className,
  titleClassName
}: AccordionWrapperProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <AccordionContext.Provider value={{ activeIndex, setActiveIndex }}>
      <div className={twMerge('w-full', className)}>
        {title && (
          <h2 className={twMerge('text-lg font-semibold mb-4', titleClassName)}>
            {title}
          </h2>
        )}
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

type AccordionProps = {
  isCollapse?: boolean;
  index?: number;
  title?: string;
  subTitle?: string;
  icons?: string[];
  iconSize?: number;
  iconColor?: string;
  children?: ReactNode;
  className?: string;
  titleClassName?: string;
  dataTestId?: string;
};

export const Accordion = ({
  isCollapse = false,
  index,
  title,
  subTitle,
  icons = ['chevron-up', 'chevron-down'],
  iconSize = 16,
  iconColor = 'fill-[#000000]',
  children,
  className,
  titleClassName,
  dataTestId
}: AccordionProps) => {
  const context = useContext(AccordionContext);
  const [localCollapse, setLocalCollapse] = useState(isCollapse);

  if (context && index !== undefined) {
    const { activeIndex, setActiveIndex } = context;
    const isActive = activeIndex === index;

    const toggleAccordion = () => {
      setActiveIndex(isActive ? null : index);
    };

    return (
      <div
        className={twMerge(
          'flex flex-col justify-center border-b pb-4 mb-4 last:border-none',
          className
        )}
      >
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={toggleAccordion}
          data-testid={dataTestId}
        >
          <div className="flex flex-col">
            {title && (
              <h3 className={twMerge('text-sm', titleClassName)}>{title}</h3>
            )}
            {subTitle && <h4 className="text-xs text-gray-700">{subTitle}</h4>}
          </div>

          {icons && (
            <Icon
              name={isActive ? icons[0] : icons[1]}
              size={iconSize}
              className={`fill-[${iconColor}]}`}
            />
          )}
        </div>
        {isActive && <div className="text-sm ">{children}</div>}
      </div>
    );
  }

  // Tek başına kullanım
  return (
    <div
      className={twMerge(
        'flex flex-col justify-center border-b pb-4 mb-4 last:border-none',
        className
      )}
    >
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setLocalCollapse(!localCollapse)}
        data-testid={dataTestId}
      >
        <div className="flex flex-col">
          {title && (
            <h3 className={twMerge('text-sm', titleClassName)}>{title}</h3>
          )}
          {subTitle && <h4 className="text-xs text-gray-700">{subTitle}</h4>}
        </div>

        {icons && (
          <Icon
            name={localCollapse ? icons[0] : icons[1]}
            size={iconSize}
            className={`fill-[${iconColor}]}`}
          />
        )}
      </div>
      {localCollapse && <div className="mt-3 text-sm">{children}</div>}
    </div>
  );
};
